/**
 * Die Vorschau eines Belegs — und der Sonderfall, um den es geht.
 *
 * PDF und Bild zeigt der Browser selbst. Eine **XRechnung hat kein Bild**: sie
 * ist reines XML, und wer sie in ein `iframe` legt, zeigt einem Menschen
 * Rohtext mit spitzen Klammern. Rechtlich gilt seit 2025 aber genau dieses XML
 * als der Beleg (Bauplan §15.1 S1). Deshalb wird daraus eine **Feldtabelle**
 * aus den ausgelesenen Feldern — Aussteller, Nummer, Betraege, IBAN, jeweils
 * mit ihrem Namen.
 *
 * Die Datei kommt mit `X-Content-Type-Options: nosniff` und
 * `Content-Security-Policy: sandbox` vom Server (§15.2): ein Beleg ist fremdes
 * Material, der Browser soll ihn anzeigen und nicht ausfuehren. Deshalb
 * `<object>` und nicht `<embed>` mit Skript-Rechten.
 */

import { FileText } from 'lucide-react';

import { bh, IST_MOCK } from '@/lib/api';
import { STEUER_HINWEIS_LABEL, vorschauart } from '@/lib/beleg';
import { dateiGroesse, datum, euro, iban as ibanFormat } from '@/lib/format';
import type { DocumentResponse } from '@/types';

export function BelegVorschau({ beleg }: { beleg: DocumentResponse }) {
  const art = vorschauart(beleg.mime_type);
  const url = bh.belegDateiUrl(beleg.id);

  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-2.5">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-slate-800">{beleg.original_name}</p>
          <p className="text-xs text-slate-400">
            {beleg.mime_type} · {dateiGroesse(beleg.size_bytes)}
          </p>
        </div>
        {art !== 'xml' && !IST_MOCK && (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="shrink-0 text-xs text-primary-700 hover:underline"
          >
            in neuem Tab öffnen
          </a>
        )}
      </div>

      <div className="p-4">
        {art === 'pdf' && (
          <object data={url} type="application/pdf" className="h-[560px] w-full rounded-lg bg-slate-100">
            {/* Faellt der Browser hierauf zurueck, kann er das PDF nicht
                einbetten — im Mock-Modus ist das der Normalfall, weil dort
                gar keine Datei liegt. */}
            <VorschauHinweis
              text={
                IST_MOCK
                  ? 'Beispieldaten: zu diesem Beleg liegt keine Datei. Mit laufender API steht hier das PDF.'
                  : 'Dieses PDF lässt sich hier nicht anzeigen.'
              }
              url={IST_MOCK ? undefined : url}
            />
          </object>
        )}

        {art === 'bild' && (
          <img
            src={url}
            alt={`Vorschau von ${beleg.original_name}`}
            className="mx-auto max-h-[560px] rounded-lg border border-slate-200 object-contain"
          />
        )}

        {art === 'xml' && <XmlFeldtabelle beleg={beleg} />}

        {art === 'unbekannt' && (
          <VorschauHinweis
            text={`Für ${beleg.mime_type} gibt es keine Vorschau.`}
            url={IST_MOCK ? undefined : url}
          />
        )}
      </div>
    </div>
  );
}

function VorschauHinweis({ text, url }: { text: string; url?: string }) {
  return (
    <div className="flex h-[560px] flex-col items-center justify-center gap-2 rounded-lg bg-slate-50 text-center">
      <FileText size={22} className="text-slate-300" />
      <p className="max-w-sm text-sm text-slate-500">{text}</p>
      {url && (
        <a href={url} target="_blank" rel="noreferrer" className="text-sm text-primary-700 hover:underline">
          Datei öffnen
        </a>
      )}
    </div>
  );
}

/**
 * Die Feldtabelle einer E-Rechnung.
 *
 * Zwei Quellen, in dieser Reihenfolge: Was der XML-Parser als `felder` in
 * `extraction` gelegt hat (dort stehen die Bezeichner der Norm, „BT-84 IBAN"),
 * und darunter die Felder des Belegs selbst. Fehlt das erste, bleibt das
 * zweite — eine leere Flaeche gaebe es nie.
 */
function XmlFeldtabelle({ beleg }: { beleg: DocumentResponse }) {
  const ausXml = xmlFelder(beleg.extraction);

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">
        Eine E-Rechnung ist reines XML und hat kein Bild. Was darin steht, liest die Buchhaltung
        deterministisch aus — ohne Modell — und zeigt es hier Feld für Feld.
      </p>

      {ausXml.length > 0 && (
        <Tabelle titel="Aus dem XML gelesen" zeilen={ausXml} />
      )}

      <Tabelle
        titel="Felder des Belegs"
        zeilen={[
          ['Aussteller', beleg.supplier_name],
          ['Anschrift', beleg.aussteller_anschrift],
          ['USt-Id', beleg.aussteller_ust_id],
          ['Steuernummer', beleg.aussteller_steuernummer],
          ['Rechnungsnummer', beleg.invoice_number],
          ['Rechnungsdatum', beleg.invoice_date ? datum(beleg.invoice_date) : null],
          ['Fällig am', beleg.due_date ? datum(beleg.due_date) : null],
          ['Leistungszeitraum', beleg.service_period],
          ['Leistung', beleg.leistung],
          ['Betrag netto', beleg.amount_net ? euro(beleg.amount_net) : null],
          ['Steuer', beleg.vat_amount ? euro(beleg.vat_amount) : null],
          ['Betrag brutto', beleg.amount_gross ? euro(beleg.amount_gross) : null],
          [
            'Steuerhinweis',
            beleg.steuer_hinweis ? STEUER_HINWEIS_LABEL[beleg.steuer_hinweis] : null,
          ],
          ['IBAN im XML', beleg.ibans_im_text?.length ? ibanFormat(beleg.ibans_im_text[0]) : null],
          [
            'Empfänger',
            beleg.empfaenger_ok === null || beleg.empfaenger_ok === undefined
              ? null
              : beleg.empfaenger_ok
                ? 'lautet auf die Elbklar GmbH'
                : 'lautet NICHT auf die Elbklar GmbH',
          ],
        ]}
      />

      {beleg.vat_lines && beleg.vat_lines.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
            Steuerzeilen
          </p>
          <table className="w-full overflow-hidden rounded-lg border border-slate-200 text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Satz</th>
                <th className="px-3 py-2 text-right">Netto</th>
                <th className="px-3 py-2 text-right">Steuer</th>
              </tr>
            </thead>
            <tbody>
              {beleg.vat_lines.map((zeile, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-3 py-2">{zeile.satz} %</td>
                  <td className="px-3 py-2 text-right tabular-nums">{euro(zeile.netto)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{euro(zeile.steuer)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Tabelle({
  titel,
  zeilen,
}: {
  titel: string;
  zeilen: [string, string | null | undefined][];
}) {
  const gefuellt = zeilen.filter(([, wert]) => wert !== null && wert !== undefined && wert !== '');
  if (gefuellt.length === 0) return null;

  return (
    <div>
      <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">{titel}</p>
      <dl className="overflow-hidden rounded-lg border border-slate-200">
        {gefuellt.map(([name, wert], i) => (
          <div
            key={name}
            className={`grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-3 px-3 py-2 text-sm ${
              i % 2 ? 'bg-white' : 'bg-slate-50/60'
            }`}
          >
            <dt className="text-slate-500">{name}</dt>
            <dd className="break-words text-slate-900">{wert}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/** `extraction.felder` als Paare — alles andere darin ist Technik-Beiwerk. */
function xmlFelder(extraction: Record<string, unknown> | null | undefined): [string, string][] {
  const felder = extraction?.felder;
  if (!felder || typeof felder !== 'object') return [];
  return Object.entries(felder as Record<string, unknown>).map(([name, wert]) => [
    name,
    String(wert),
  ]);
}
